import { useCallback, useMemo, type ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { IntroReveal } from './components/intro/IntroReveal'
import { AppShell } from './components/layout/AppShell'
import { ErrorState, LoadingState } from './components/ui/DataState'
import { useAuth } from './context/AuthContext'
import { LeadsSkeleton } from './features/leads/LeadsSkeleton'
import { useCrmData, type CrmDataState, type RefreshCrmDataOptions } from './hooks/useCrmData'
import { useProspectingData } from './hooks/useProspectingData'
import { useUnifiedLeads } from './hooks/useUnifiedLeads'
import { AnalyticsPage } from './pages/AnalyticsPage'
import { DashboardPage } from './pages/DashboardPage'
import { LeadsPage } from './pages/LeadsPage'
import { LoginPage } from './pages/LoginPage'
import { ProfilePage } from './pages/ProfilePage'
import { ProspectingPage } from './pages/ProspectingPage'
import { SalesTeamPage } from './pages/SalesTeamPage'

function CrmApplication({ completeIntro, introPending }: { completeIntro: () => void; introPending: boolean }) {
  const dataState = useCrmData()
  // The prospecting workbook is loaded once for the whole application: the main
  // Leads page unifies it with the CRM, and the Prospecting page uses the CRM
  // dataset to resolve "Also in Leads".
  const prospectingState = useProspectingData()
  const { leads, crmMembership } = useUnifiedLeads(dataState.data, prospectingState.data)

  const crmRefresh = dataState.refresh
  const prospectingRefresh = prospectingState.refresh
  const refresh = useCallback((options?: RefreshCrmDataOptions) => {
    crmRefresh(options)
    prospectingRefresh()
  }, [crmRefresh, prospectingRefresh])

  const shellState: CrmDataState = useMemo(() => ({
    ...dataState,
    refresh,
    refreshing: dataState.refreshing || prospectingState.refreshing,
  }), [dataState, prospectingState.refreshing, refresh])

  // The route always switches at once; until the Sheet arrives the destination
  // shows its own loading state (or the error), never the previous page.
  const data = dataState.data
  const pending = (loading: ReactNode = <LoadingState />) => dataState.status === 'error'
    ? <ErrorState message={dataState.error ?? 'Unknown data-source error.'} onRetry={refresh} />
    : loading

  return (
    <>
      {introPending ? <Navigate replace to="/dashboard" /> : null}
      <AppShell dataState={shellState}>
        <Routes>
          <Route path="/dashboard" element={data ? <DashboardPage dataset={data} /> : pending()} />
          <Route path="/leads" element={data ? <LeadsPage leads={leads} /> : pending(<LeadsSkeleton />)} />
          <Route path="/sales-team" element={data ? <SalesTeamPage leads={leads} /> : pending()} />
          <Route path="/analytics" element={data ? <AnalyticsPage dataset={data} /> : pending()} />
          <Route path="/profile" element={data ? <ProfilePage dataState={dataState} /> : pending()} />
          <Route
            path="/prospecting"
            element={data ? <ProspectingPage crmMembership={crmMembership} dataState={prospectingState} /> : pending()}
          />
          <Route path="/settings" element={<Navigate replace to="/profile" />} />
          <Route path="*" element={<Navigate replace to="/dashboard" />} />
        </Routes>
      </AppShell>
      {introPending ? <IntroReveal dataStatus={dataState.status} onComplete={completeIntro} /> : null}
    </>
  )
}

export default function App() {
  const { authenticated, checkingSession, completeIntro, introPending } = useAuth()
  if (checkingSession) {
    return (
      <main className="state-screen" role="status">
        <h1>Checking secure session</h1>
        <p>Verifying protected Wolf Forge CRM access.</p>
        <div aria-hidden="true" className="loading-line"><span /></div>
      </main>
    )
  }
  return authenticated ? <CrmApplication completeIntro={completeIntro} introPending={introPending} /> : <LoginPage />
}
